import { useCallback, useEffect, useRef, useState } from 'react';
import { useKeycloak } from '../../../authentication/keycloak/KeycloakProvider';
import {
  getDashboardOfficeDetail,
  getDashboardOverview,
  getDashboardReporting,
  getDefaultThreshold,
  getDisplays,
  getEmployeePresence,
  getEmployeePresenceDays,
  type ReportingFilter,
} from '../services/dashboard.service';
import {
  type DashboardOfficeDetail,
  type DashboardOverview,
  type DashboardReporting,
  type DefaultThreshold,
  type Display,
  type EmployeePresence,
  type EmployeePresenceDetail,
  type ReportingComparisonMode,
} from '../models/dashboard.models';

// The hooks below expose a manual refresh() alongside their effect-driven fetch, so a
// per-effect `cancelled` flag isn't enough: two in-flight requests can also overlap because the
// user changed a filter and hit refresh, or because a slow response for a large office lands after
// a fast one for a small office. Each fetch takes a monotonic request id and only commits if it is
// still the most recent one, so a stale response can never overwrite newer data.
const useLatestRequestGuard = (): (() => () => boolean) => {
  const requestIdRef = useRef(0);

  return useCallback(() => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    return (): boolean => requestIdRef.current === requestId;
  }, []);
};

interface DashboardOverviewState {
  readonly initialized: boolean;
  readonly overview?: DashboardOverview;
  readonly error?: boolean;
}

export const useDashboardOverview = (
  date?: string,
  // Bump this (e.g. after a default-threshold save elsewhere in the app) to force a refetch even
  // though this tab stays mounted-but-hidden and would otherwise never know its data went stale.
  dataVersion = 0,
): DashboardOverviewState & { refresh: () => void } => {
  const [state, setState] = useState<DashboardOverviewState>({ initialized: false });
  const keycloak = useKeycloak();

  const beginRequest = useLatestRequestGuard();

  const fetchOverview = useCallback(async (): Promise<void> => {
    const isLatest = beginRequest();
    const overview = await getDashboardOverview(keycloak, date);
    if (!isLatest()) {
      return;
    }

    setState({ initialized: true, overview, error: overview === undefined });
  }, [beginRequest, keycloak, date]);

  useEffect(() => {
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOverview, dataVersion]);

  return { ...state, refresh: fetchOverview };
};

interface DashboardOfficeDetailState {
  readonly initialized: boolean;
  readonly detail?: DashboardOfficeDetail;
  readonly error?: boolean;
}

export const useDashboardOfficeDetail = (
  officeId?: string,
  date?: string,
  // Bump this (e.g. after a default-threshold save elsewhere in the app) to force a refetch even
  // though this tab stays mounted-but-hidden and would otherwise never know its data went stale.
  dataVersion = 0,
): DashboardOfficeDetailState & { refresh: () => void } => {
  const [state, setState] = useState<DashboardOfficeDetailState>({ initialized: false });
  const keycloak = useKeycloak();

  const beginRequest = useLatestRequestGuard();

  const fetchDetail = useCallback(async (): Promise<void> => {
    const isLatest = beginRequest();
    if (!officeId) {
      setState({ initialized: true });
      return;
    }

    const detail = await getDashboardOfficeDetail(officeId, keycloak, date);
    if (!isLatest()) {
      return;
    }

    setState({ initialized: true, detail, error: detail === undefined });
  }, [beginRequest, keycloak, officeId, date]);

  useEffect(() => {
    setState({ initialized: false });
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDetail, dataVersion]);

  return { ...state, refresh: fetchDetail };
};

interface DashboardReportingState {
  readonly initialized: boolean;
  readonly reporting?: DashboardReporting;
  readonly error?: boolean;
  /// <summary>401 means the caller has no accessible offices for the current filter (e.g. an
  /// empty/foreign company selection) rather than a genuine backend failure — worth messaging
  /// differently so the user knows to just pick a different filter, not that something broke.</summary>
  readonly errorStatus?: number;
}

export const useDashboardReporting = (
  startDate: string,
  endDate: string,
  comparison: ReportingComparisonMode,
  filter?: ReportingFilter,
): DashboardReportingState => {
  const [state, setState] = useState<DashboardReportingState>({ initialized: false });
  const keycloak = useKeycloak();

  const officeId = filter?.officeId;
  const companyId = filter?.companyId;
  const officeIdsKey = filter?.officeIds?.join(',');
  const compareOffices = filter?.compareOffices;

  useEffect(() => {
    let cancelled = false;
    setState({ initialized: false });

    const fetchReporting = async (): Promise<void> => {
      const { reporting, status } = await getDashboardReporting(startDate, endDate, comparison, keycloak, {
        officeId,
        companyId,
        officeIds: officeIdsKey ? officeIdsKey.split(',') : undefined,
        compareOffices,
      });
      if (!cancelled) {
        const hasError = reporting === undefined;
        setState({ initialized: true, reporting, error: hasError, errorStatus: hasError ? status : undefined });
      }
    };

    fetchReporting();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak, startDate, endDate, comparison, officeId, companyId, officeIdsKey, compareOffices]);

  return state;
};

interface EmployeePresenceState {
  readonly initialized: boolean;
  readonly employees?: EmployeePresence[];
  readonly error?: boolean;
}

export const useEmployeePresence = (
  startDate: string,
  endDate: string,
  filter?: ReportingFilter,
  // The Reporting tab passes false while the custom range is end-before-start: the backend would
  // just 400, and the tab already explains the problem inline.
  enabled = true,
): EmployeePresenceState => {
  const [state, setState] = useState<EmployeePresenceState>({ initialized: false });
  const keycloak = useKeycloak();

  const officeId = filter?.officeId;
  const companyId = filter?.companyId;
  const officeIdsKey = filter?.officeIds?.join(',');

  useEffect(() => {
    if (!enabled) {
      setState({ initialized: false });
      return undefined;
    }

    let cancelled = false;
    setState({ initialized: false });

    const fetchEmployeePresence = async (): Promise<void> => {
      const employees = await getEmployeePresence(startDate, endDate, keycloak, {
        officeId,
        companyId,
        officeIds: officeIdsKey ? officeIdsKey.split(',') : undefined,
      });
      if (!cancelled) {
        setState({ initialized: true, employees, error: employees === undefined });
      }
    };

    fetchEmployeePresence();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak, startDate, endDate, officeId, companyId, officeIdsKey, enabled]);

  return state;
};

interface EmployeePresenceDaysState {
  readonly initialized: boolean;
  readonly detail?: EmployeePresenceDetail;
  readonly error?: boolean;
}

/// <summary>Backs the drill-down modal. Stays idle until an employee is actually selected, so
/// opening the Reporting tab costs one request per panel, not one per row.</summary>
export const useEmployeePresenceDays = (
  officeUserId: string | undefined,
  startDate: string,
  endDate: string,
  filter?: ReportingFilter,
): EmployeePresenceDaysState => {
  const [state, setState] = useState<EmployeePresenceDaysState>({ initialized: false });
  const keycloak = useKeycloak();

  const officeId = filter?.officeId;
  const companyId = filter?.companyId;
  const officeIdsKey = filter?.officeIds?.join(',');

  useEffect(() => {
    if (!officeUserId) {
      setState({ initialized: false });
      return undefined;
    }

    let cancelled = false;
    setState({ initialized: false });

    const fetchDays = async (): Promise<void> => {
      const detail = await getEmployeePresenceDays(officeUserId, startDate, endDate, keycloak, {
        officeId,
        companyId,
        officeIds: officeIdsKey ? officeIdsKey.split(',') : undefined,
      });
      if (!cancelled) {
        setState({ initialized: true, detail, error: detail === undefined });
      }
    };

    fetchDays();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak, officeUserId, startDate, endDate, officeId, companyId, officeIdsKey]);

  return state;
};

interface DefaultThresholdState {
  readonly initialized: boolean;
  readonly threshold?: DefaultThreshold;
  readonly error?: boolean;
}

export const useDefaultThreshold = (companyId?: string): DefaultThresholdState & { refresh: () => void } => {
  const [state, setState] = useState<DefaultThresholdState>({ initialized: false });
  const keycloak = useKeycloak();

  const beginRequest = useLatestRequestGuard();

  const fetchThreshold = useCallback(async (): Promise<void> => {
    const isLatest = beginRequest();
    const threshold = await getDefaultThreshold(keycloak, companyId);
    if (!isLatest()) {
      return;
    }

    setState({ initialized: true, threshold, error: threshold === undefined });
  }, [beginRequest, keycloak, companyId]);

  useEffect(() => {
    setState({ initialized: false });
    fetchThreshold();
  }, [fetchThreshold]);

  return { ...state, refresh: fetchThreshold };
};

interface DisplaysState {
  readonly initialized: boolean;
  readonly displays?: Display[];
  readonly error?: boolean;
}

export const useDisplays = (officeId?: string): DisplaysState & { refresh: () => void } => {
  const [state, setState] = useState<DisplaysState>({ initialized: false });
  const keycloak = useKeycloak();

  const beginRequest = useLatestRequestGuard();

  const fetchDisplays = useCallback(async (): Promise<void> => {
    const isLatest = beginRequest();
    if (!officeId) {
      setState({ initialized: true, displays: [] });
      return;
    }

    const displays = await getDisplays(officeId, keycloak);
    if (!isLatest()) {
      return;
    }

    setState({ initialized: true, displays, error: displays === undefined });
  }, [beginRequest, keycloak, officeId]);

  useEffect(() => {
    fetchDisplays();
  }, [fetchDisplays]);

  return { ...state, refresh: fetchDisplays };
};
